import GoogleAnalytics from 'react-ga';

import { getAuthHeaders, setToken, logout, isUserOnLoginPage } from './authorizator';
import { displayGlobalMessage } from '../actions/globalMessage';
import { redirectToPageNotFound, redirectToPageFeatureUnavailable } from '../actions/shared';
import { isIos } from './browser.js';
import { getServerErrorMessage } from './serverErrorMessageCatalogue';
import { AUTHENTICATE_API } from 'actions/user.js';
import { queueESEvents, ESEventTypes } from './logEventToElasticSearch';

export class FetchInterceptor {
  get(url, params, options) {
    return this.processRequest(url, { ...params, method: 'get' });
  }

  post(url, params, options) {
    return this.processRequest(url, { ...params, method: 'post' }, options);
  }

  put(url, params, options) {
    return this.processRequest(url, { ...params, method: 'put' });
  }

  delete(url, params, options) {
    return this.processRequest(url, { ...params, method: 'delete' });
  }

  //process the request and returns an appropriatre result or throw an error
  processRequest(url, params, options) {
    if (!window.fetchPromiseController) {
      try {
        const controller = new AbortController();
        const signal = controller.signal;
        window.fetchPromiseController = controller;
      } catch (abortControllerErr) {
        console.error(abortControllerErr);
        window.fetchPromiseController = null;
        let errorString = '';
        try {
          errorString = abortControllerErr.toString();
          if (errorString === '[object Object]') {
            errorString = abortControllerErr;
          }
        } catch (toStringErr) {
          errorString = '';
        }
        queueESEvents(ESEventTypes.AbortControllerError, errorString, 'eventType');
      }
    }
    const fetchRequestTimestamp = new Date().getTime();
    return fetch(url, {
      headers: getAuthHeaders(),
      signal: !!window.fetchPromiseController ? window.fetchPromiseController.signal : null,
      ...params,
    })
      .then((response) => {
        var timeTakenByFetchApi = new Date().getTime() - fetchRequestTimestamp;
        console.log(`Fetch API operation completed in ${timeTakenByFetchApi} ms`, url);
        if (options && options.cb) {
          options.cb();
        } else {
          // TODO - Log this into New Relic instead (after 3.9.1 is out) - 'VCM Fetch API - Success' - ${timeTakenByFetchApi}
          return this.processResponse(response, url);
        }
      })
      .catch((err, response) => {
        var timeTakenByFetchApi = new Date().getTime() - fetchRequestTimestamp;
        console.error(`Fetch API operation failed in ${timeTakenByFetchApi} ms`, url, err, response);

        if (options && options.ignoreDisplayErrors === true) {
          options.errorCallback();
        } else {
          // If error is caused by aborting a api, do not show red message
          if (
            (window.fetchPromiseController &&
              window.fetchPromiseController.signal &&
              !!window.fetchPromiseController.signal.aborted) ||
            (err.toString && err.toString().indexOf('aborted a request') > -1)
          ) {
            return;
          } else {
            // TODO - Log this into New Relic instead (after 3.9.1 is out) - 'VCM Fetch API - Error' - ${timeTakenByFetchApi}
            console.log('The error is cause by', err, isIos());
            try {
              if (err && err.status) {
                let additionalValue = {
                  apiURL: url,
                  error: err.status,
                  browserURL: window.location.href,
                };
                queueESEvents(ESEventTypes.FetchAPIError, 'API Error', 'error&apiURL', additionalValue);
              } else {
                let errorString = '';
                try {
                  errorString = err.toString();
                  if (errorString === '[object Object]') {
                    errorString = err;
                  }
                } catch (toStringErr) {
                  errorString = '';
                }
                let additionalValue = {
                  apiURL: url,
                  error: errorString,
                  browserURL: window.location.href,
                };
                queueESEvents(ESEventTypes.FetchAPIError, 'API Error', 'error&apiURL', additionalValue);
              }
            } catch (logErr) {
              console.log('Queue events for logging error', logErr);
            }
            if (
              err.message &&
              (err.message.toLowerCase() === 'failed to fetch' ||
                (isIos() && err.message.toString().toLowerCase().indexOf('type error') >= 0))
            ) {
              //means there is a network connectivity problem.
              //the type error is for ios, as in ios it says 'type error' instead of 'failed to fetch'

              let error = APP.LANGUAGE.Errors.offlineFetchApi.replace(
                /##portalName##/g,
                window.APP.CONFIGURATION.PORTAL.Name
              );

              let buttonText = APP.LANGUAGE.Errors.offlineFetchApiBtnText.replace(
                /##portalName##/g,
                window.APP.CONFIGURATION.PORTAL.Name
              );
              FetchInterceptor.dispatch(
                displayGlobalMessage({
                  text: error,
                  visible: true,
                  messageType: APP.LANGUAGE.GlobalMessageTypes.Bad,
                  autoHide: false,
                  gotItButtonText: buttonText,
                  gotItCallBack: () => {
                    window.open(APP.EXTERNAL_LINKS.Login.HealthCheckStatusLink, '_blank');
                  },
                  apiURL: url,
                  browserURL: window.location.href,
                })
              );

              err.message = 'failed to fetch'; //setting to this error to make consistent. Login page will not show invalid password now
            } else if (err.status !== 404) {
              let error = getServerErrorMessage(err);
              if (error.indexOf('##checkHealthStatusLink##') > -1) {
                FetchInterceptor.dispatch(
                  displayGlobalMessage({
                    text: APP.LANGUAGE.ServerErrors.OtherGeneralApiError,
                    visible: true,
                    messageType: APP.LANGUAGE.GlobalMessageTypes.Bad,
                    autoHide: false,
                    linkText: APP.LANGUAGE.ServerErrors.OtherGeneralApiErrorLink,
                    linkCallBack: () => {
                      window.open(APP.EXTERNAL_LINKS.Login.HealthCheckStatusLink, '_blank');
                    },
                    apiURL: url,
                    browserURL: window.location.href,
                  })
                );
              } else {
                FetchInterceptor.dispatch(
                  displayGlobalMessage({
                    text: error,
                    visible: true,
                    messageType: APP.LANGUAGE.GlobalMessageTypes.Bad,
                    autoHide: false,
                    apiURL: url,
                    browserURL: window.location.href,
                  })
                );
              }
            }
          }
        }

        throw err; //returns the exception to the code for logging or any further action
      });
  }

  //process the responsose
  processResponse(response, url) {
    const loginApiUrl = window.APP.CONFIGURATION.VCCAPI + AUTHENTICATE_API;
    if (response.ok) {
      //if the response is ok

      // check if new session token is made available
      setToken(response, FetchInterceptor.dispatch);

      return response
        .json()
        .then((data) => {
          return data;
        })
        .catch((err) => {
          //means from api it could happen result status is ok, but no result/content is returned.
        });
    } else if (response.status === 401 && !isUserOnLoginPage()) {
      // general error in case of authentication, if not on login page, then logout and go to login page
      logout(FetchInterceptor.dispatch);
    } else if (response.status === 401 && isUserOnLoginPage() && loginApiUrl !== url) {
      console.log(
        'No further action required - server says login required   AND   user is already on login view   AND   this API call was not login api'
      );
      // No further action required, when
      //        server says login required
      //  AND   user is already on login view
      //  AND   this API call was not login api
      return;
    } else if (response.status === 404) {
      //we will throw the error from now on in case of 404.
      //if user wants to do processing or show any other page based upon 404, they can handle that
      throw response;
    } else if (response.status === 302) {
      // Handles payment required to access this resource
      queueESEvents(ESEventTypes.InsufficientSubscribeLevel.replace('##url##', url), `true, status=${response.status}`);
      FetchInterceptor.dispatch(redirectToPageFeatureUnavailable(true));
    } else {
      //if there is error we hide the overlay.
      window.$('#overlay').hide();
      //throw the statusText as error. it can happen in case of duplicate name, or any other scnario
      if (response.statusText && response.statusText != '') {
        throw response.statusText;
      }
      //if the statusText is empty, there could be error in the textbody of the response.
      //as we only come here when we don't have 200 response, so we understand there could be an error.
      return response
        .text()
        .then((data) => {
          //translating the error
          throw data;
        })
        .catch((err) => {
          //if error is empty we thorw the response.status
          if (err != '') throw JSON.parse(err);

          //if statusText is empty and there is no response text body we through the status as exception.
          throw response.status;
        });
    }
  }
}

FetchInterceptor.dispatch = undefined;
export default new FetchInterceptor();
